/**
 * `input[type=text]` (process/08-COMPONENT-CATALOGUE.md's "No component" reasoning applies here
 * too, though the catalogue itself does not list it: a plain text input inside `field` carries no
 * `data-cmp` of its own — `field` is the addressable, catalogued unit, and the input is its
 * uncatalogued internal control, the same relationship `otp-input`'s six `<input>`s have to it).
 *
 * Not a component (no JSX, nothing for `data-cmp-required` to check) — a class-string constant,
 * reused by every screen that needs a plain text field instead of extracting a wrapper component
 * for a DOM element the catalogue does not name.
 *
 * border 1px solid var(--bd2) (width not a token family); border-radius 3px → --r-1 (0 delta);
 * height 32px (out of scope); padding 0 9px → --sp-0 / --sp-5 (+1px delta); font-size 12.5px →
 * --fs-body-lg (+0.5px delta).
 */
export const TEXT_INPUT_CLASSNAME =
  'w-full border border-bd2 rounded-1 py-0 px-5 text-body-lg text-tx outline-none focus:border-blue focus:shadow-focus'

export const TEXT_INPUT_STYLE = { height: '32px' } as const

/**
 * `.control` (`Prototype_v2.html`) → `field-control` (`process/08-COMPONENT-CATALOGUE.md`) — the
 * bordered, filled box a field's own control sits in: `display:flex;align-items:center;gap:8px;
 * height:32px;border:1px solid var(--bd2);border-radius:3px;background:#fff;padding:0 8px;
 * font-size:12.5px;color:var(--tx)`. Not a component (no JSX) — the same class-string-constant
 * convention `TEXT_INPUT_CLASSNAME` above already uses, since `field-control` is a real catalogued
 * `data-cmp` with no dedicated component of its own; every screen had been building it by hand,
 * several with no box styling at all (found by `semantic-diff.mjs`: `field-control`'s own computed
 * background/border read transparent/`null` against the prototype's real filled, bordered box).
 *
 * A read-only `field-control` (wrapping a `Chip`, a plain value) takes this class directly. One
 * wrapping a real `<select>`/`<input>` takes it too, with the nested control itself demoted to
 * `FIELD_CONTROL_NATIVE_CLASSNAME` below — the box is the one visual unit; the native element's own
 * border/background/padding would otherwise double up inside it.
 */
export const FIELD_CONTROL_CLASSNAME = 'flex items-center gap-2 h-8 border border-bd2 rounded-1 bg-panel px-4 text-body-lg text-tx'

export const FIELD_CONTROL_NATIVE_CLASSNAME = 'w-full h-full border-0 bg-transparent p-0 text-body-lg text-tx outline-none'
